import moment from 'moment'
import { createSelector } from 'APP/Redux/utils'

import { AllProviderMatchingAttributes, AvailabilitySlot } from '@dialogue/timekeeper'

import type { RootState } from 'APP/Store/CreateStore'
import { DATE_FORMAT } from 'APP/Redux/AppointmentBookingRedux'

export const selectLoadingAvailabilities = (state: RootState) =>
  state.providerBooking.availabilities.loading
export const selectAvailabilitiesError = (state: RootState) =>
  state.providerBooking.availabilities.error
export const selectProvider = (state: RootState, id: number) =>
  state.providerBooking.providers.data[id]
export const selectProviderById = (state: RootState) => state.providerBooking.providerDetails?.data
export const selectLoadingProviders = (state: RootState) =>
  state.providerBooking.providers.loadingList
export const selectLoadingProviderBio = (state: RootState) =>
  state.providerBooking.providers.loadingBio
export const selectProvidersAttributesLoading = (state: RootState) =>
  state.providerBooking.attributes.loading
export const selectProvidersAttributesError = (state: RootState) =>
  state.providerBooking.attributes.error
export const selectProvidersAttributes = (state: RootState) => state.providerBooking.attributes.data

export const selectCreateAppointmentRequest = (state: RootState) =>
  state.providerBooking.createAppointmentRequest

export const selectBooking = (state: RootState) => state.providerBooking.booking.data
export const selectBookingLoading = (state: RootState) => state.providerBooking.booking.loading
export const selectBookingError = (state: RootState) => state.providerBooking.booking.error

export const selectSelectedDay = (state: RootState) => state.providerBooking.selectedDay

export const selectInitialAvailabilities = (state: RootState) =>
  state.providerBooking.availabilities.data

const NO_AVAILABILITIES: AvailabilitySlot[] = []

const selectProviderAvailabilitiesState = (state: RootState, providerId: number) =>
  state.providerBooking.providerAvailabilities[providerId]

export const selectProviderAvailabilities = (state: RootState, providerId: number) =>
  selectProviderAvailabilitiesState(state, providerId)?.data ?? NO_AVAILABILITIES

export const selectLoadingProviderAvailabilities = (state: RootState, providerId: number) =>
  selectProviderAvailabilitiesState(state, providerId)?.loading ?? false

export const selectAvailabilityPerDateWithTime = createSelector(
  [selectInitialAvailabilities],
  (availabilities) => {
    const availabilitiesWithDateTime = new Map<string, { start_date: string; start_time: string }>()

    availabilities.forEach((slot) => {
      const date = moment(slot.start_at).format('YYYY-MM-DD')
      const time = moment(slot.start_at).format('HH:mm:ss')
      const existingSlot = availabilitiesWithDateTime.get(date)
      const earlierSlot =
        existingSlot &&
        moment(slot.start_at).isBefore(
          moment(existingSlot.start_date + 'T' + existingSlot.start_time)
        )

      if (!existingSlot || earlierSlot) {
        availabilitiesWithDateTime.set(date, { start_date: date, start_time: time })
      }
    })

    return Array.from(availabilitiesWithDateTime.values())
  }
)

export const selectProviderPerDayAvailabilities = createSelector(
  [selectInitialAvailabilities, selectSelectedDay],
  (availabilities, selectedDay) =>
    availabilities.reduce<AvailabilitySlot[]>((acc, curr) => {
      const slotDay = moment(curr.start_at).format(DATE_FORMAT)
      if (slotDay !== selectedDay) return acc
      const alreadyIncluded = acc.some(
        (item: AvailabilitySlot) => item.provider_id === curr.provider_id
      )
      return alreadyIncluded ? acc : [...acc, curr]
    }, [])
)

export const selectProvidersAttributesByKeys = createSelector(
  [selectProvidersAttributes, (_, locale: string) => locale],
  (attributes, locale) => {
    if (!attributes) return {}
    return Object.values(attributes)
      .flat()
      .reduce((acc, curr) => ({ ...acc, [curr.id]: curr[locale] }), {})
  }
)

const resolveAttributeLabels = <T extends { id: string }>(
  ids: string[] = [],
  attributes: T[] | undefined
): T[] =>
  ids
    .map((id) => attributes?.find((attribute) => attribute.id === id))
    .filter((attribute): attribute is T => Boolean(attribute))

/**
 * Joins the booking's member_constraints ids against the fetched provider attributes to
 * rebuild the `{ id, en, fr }` objects (e.g. for display or the member_preference
 * payload). Returns null when there is no booking.
 */
export const selectSelectedPreferencesWithLabels = createSelector(
  [selectBooking, selectProvidersAttributes],
  (booking, attributes): AllProviderMatchingAttributes | null => {
    if (!booking) return null

    const constraints = booking.member_constraints

    return {
      genders: resolveAttributeLabels(constraints?.genders, attributes?.genders),
      cultural_backgrounds: resolveAttributeLabels(
        constraints?.cultural_backgrounds,
        attributes?.culturalBackgrounds
      ),
      additional_interests: resolveAttributeLabels(
        constraints?.additional_interests,
        attributes?.additionalInterests
      ),
    }
  }
)

/**
 * Whether the booking has any member-selected matching preferences
 * (gender, cultural background, or additional interest).
 */
export const selectHasBookingPreferences = createSelector([selectBooking], (booking): boolean => {
  const constraints = booking?.member_constraints
  return Boolean(
    constraints?.genders?.length ||
      constraints?.cultural_backgrounds?.length ||
      constraints?.additional_interests?.length
  )
})

export const selectSelectedProviderId = (state: RootState) =>
  state.providerBooking.selectedProviderId

export const selectAppointmentTypesData = (state: RootState) =>
  state.providerBooking.appointmentTypes.data
export const selectLoadingAppointmentTypes = (state: RootState) =>
  state.providerBooking.appointmentTypes.loading

export const selectAppointmentTypesFetched = (state: RootState) =>
  state.providerBooking.appointmentTypes.fetched

export const selectAppointmentTypes = createSelector([selectAppointmentTypesData], (types) =>
  types.map((type) => type.appointment_type)
)
